import React, { useEffect, useRef } from 'react';
import { List, MousePointerClick, Activity, Zap } from 'lucide-react';
import { gsap } from 'gsap';
import { trackVideoView } from '../utils/meta-pixel';
import { trackRegister } from '../utils/meta-pixel';

const VideoViewOverlay: React.FC<{ contentName: string }> = ({ contentName }) => {
  const [isRevealed, setIsRevealed] = React.useState(false);
  const handleClick = () => {
    trackVideoView(contentName);
    setIsRevealed(true);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };
  if (isRevealed) return null;
  return (
    <div
      className="absolute inset-0 z-10 cursor-pointer"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label="Assistir vídeo"
    />
  );
};

const Hero: React.FC = () => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const whatsappRef = useRef<HTMLSpanElement>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const heroSectionRef = useRef<HTMLElement>(null);
  
  const ligueRef = useRef<HTMLSpanElement>(null);
  const brasilRef = useRef<HTMLSpanElement>(null);
  const cliqueRef = useRef<HTMLSpanElement>(null);
  const navegadorRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Entrada triunfal do texto
      const tl = gsap.timeline({ delay: 0.2 });
      
      tl.fromTo('.hero-badge', 
        { opacity: 0, scale: 0.9, y: 10 },
        { opacity: 1, scale: 1, y: 0, duration: 0.6, ease: "back.out(1.7)" }
      );

      tl.fromTo([ligueRef.current, brasilRef.current, cliqueRef.current, navegadorRef.current, whatsappRef.current],
        { opacity: 0, y: 30, filter: "blur(10px)" },
        { opacity: 1, y: 0, filter: "blur(0px)", duration: 0.8, stagger: 0.1, ease: "power4.out" },
        "-=0.4"
      );

      tl.fromTo('.hero-subtitle',
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" },
        "-=0.6"
      );

      tl.fromTo('.hero-ctas',
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" },
        "-=0.6"
      );

      // Flutuação das luzes de fundo (Ambient Glow)
      gsap.to('.glow-1', {
        x: '30%', y: '20%', duration: 8, repeat: -1, yoyo: true, ease: "sine.inOut"
      });
      gsap.to('.glow-2', {
        x: '-20%', y: '30%', duration: 10, repeat: -1, yoyo: true, ease: "sine.inOut", delay: 1
      });

      // Animação dos Floating Cards
      gsap.to('.floating-card', {
        y: "-=15",
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut",
        stagger: { each: 0.5 }
      });

      // Animação da Seta e Texto de Apoio
      gsap.to('.hero-helper-float', {
        y: "+=10",
        x: "+=5",
        rotation: "-=2",
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut"
      });
    }, heroSectionRef);

    return () => ctx.revert();
  }, []);

  const [mousePos, setMousePos] = React.useState({ x: 0, y: 0 });
  const sectionRef = React.useRef<HTMLElement>(null);

  // Memoriza os valores das barras para evitar que mudem a cada re-render (mouse move)
  const waveBars = React.useMemo(() => {
    return Array.from({ length: 60 }).map(() => ({
      height: `${20 + Math.random() * 80}px`,
      opacity: 0.3 + Math.random() * 0.7
    }));
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  return (
    <section 
      ref={heroSectionRef}
      id="home" 
      onMouseMove={handleMouseMove}
      className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-white group/hero"
    >
      {/* 1. Background Estilizado - Grid + Glows */}
      <div className="absolute inset-0 pointer-events-none" ref={sectionRef as any}>
        {/* Camada de Cor Base - Branca com Slate suave */}
        <div className="absolute inset-0 bg-white" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,#f8fafc_0%,transparent_100%)]" />
        
        {/* Grid de Linhas Base - Mais visível quando apagado */}
        <div className="absolute inset-0 opacity-[0.05]" 
          style={{ 
            backgroundImage: `
              linear-gradient(to right, #3b82f6 1px, transparent 1px),
              linear-gradient(to bottom, #3b82f6 1px, transparent 1px)
            `, 
            backgroundSize: '40px 40px' 
          }} 
        />

        {/* Grid de Linhas Hover - Mais suave e com mais blur */}
        <div className="absolute inset-0 opacity-0 group-hover/hero:opacity-[0.15] transition-opacity duration-700" 
          style={{ 
            backgroundImage: `
              linear-gradient(to right, #3b82f6 1px, transparent 1px),
              linear-gradient(to bottom, #3b82f6 1px, transparent 1px)
            `, 
            backgroundSize: '40px 40px',
            WebkitMaskImage: `radial-gradient(circle 350px at ${mousePos.x}px ${mousePos.y}px, black 0%, transparent 100%)`,
            maskImage: `radial-gradient(circle 350px at ${mousePos.x}px ${mousePos.y}px, black 0%, transparent 100%)`,
          }} 
        />

        {/* Ambient Glows Dinâmicos - Tons Premium e muito sutis */}
        <div className="glow-1 absolute top-[-5%] left-[-5%] w-[40%] h-[40%] rounded-full bg-indigo-500/[0.03] blur-[100px] animate-pulse" />
        <div className="glow-2 absolute bottom-[5%] right-[-5%] w-[40%] h-[40%] rounded-full bg-blue-500/[0.03] blur-[100px]" />
        <div className="glow-3 absolute top-[15%] right-[5%] w-[30%] h-[30%] rounded-full bg-violet-500/[0.03] blur-[80px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mx-auto">
          {/* Badge Premium */}
          <div className="hero-badge inline-flex items-center px-3 py-1 rounded-full text-[10px] font-medium bg-gray-50 border border-gray-100 text-gray-500 mb-8">
            <span className="mr-1.5">🏆</span>
            Discador WebRTC #1 do Brasil
          </div>

          {/* Main Title - Ultra Modern Typography */}
          <h1
            ref={titleRef}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.2] lg:leading-[1.1] mb-6 sm:mb-8 tracking-tight text-gray-900 px-2 sm:px-0"
          >
            <span ref={ligueRef} className="inline-block">Ligue para todo o Brasil</span>{' '}
            <span ref={cliqueRef} className="inline-block underline decoration-blue-600/30 underline-offset-4">com 1 clique</span>{' '}
            <span ref={navegadorRef} className="inline-block">direto do seu</span>{' '}
            <span ref={whatsappRef} className="inline-block">
              <span className="bg-gradient-to-r from-sky-500 via-blue-600 to-indigo-600 bg-clip-text text-transparent animate-gradient">
                Navegador
              </span>
            </span>
          </h1>

          {/* Subtitle */}
          <p className="hero-subtitle text-sm sm:text-base lg:text-lg text-gray-600 mb-6 max-w-2xl mx-auto leading-relaxed px-4">
            O discador web com BINA inteligente e IA para transcrever e analisar chamadas, gerando insights práticos de qualidade e performance.
          </p>

          <p className="hero-target text-[11px] sm:text-[13px] font-bold text-blue-600 mb-8 sm:mb-10 tracking-widest uppercase px-4">
            Ideal para times de Inside Sales, CS, CX, Suporte Técnico
          </p>

          {/* CTA Buttons com Shimmer Effect */}
          <div className="hero-ctas flex flex-col sm:flex-row gap-4 mb-16 sm:mb-20 justify-center items-center px-4">
            <a
              href="https://flow.onmai.app.br/register"
              onClick={() => trackRegister()}
              className="group relative overflow-hidden inline-flex items-center justify-center w-full sm:w-auto px-8 sm:px-10 py-4 bg-gray-900 text-white font-bold rounded-2xl transition-all duration-300 hover:scale-105 hover:bg-blue-600 shadow-xl shadow-blue-900/10 sm:min-w-[280px]"
              aria-label="Receba R$ 1 para testar agora - cadastro"
            >
              {/* Shimmer Effect */}
              <div className="absolute inset-0 w-1/2 h-full bg-white/10 skew-x-[-25deg] translate-x-[-150%] group-hover:translate-x-[250%] transition-transform duration-1000 ease-in-out" />
              
              <span className="relative z-10 flex items-center text-sm sm:text-base">
                Receba R$ 1 para testar agora
                <MousePointerClick className="ml-2.5 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </a>
            
            <a 
              href="#features"
              className="group inline-flex items-center justify-center w-full sm:w-auto px-8 sm:px-10 py-4 bg-white text-gray-700 font-bold rounded-2xl border border-gray-200 transition-all duration-300 hover:bg-gray-50 hover:border-gray-300 sm:min-w-[280px]"
            >
              <List className="mr-2.5 w-5 h-5 text-gray-400 group-hover:text-gray-600" />
              <span className="text-sm sm:text-base">Veja os recursos</span>
            </a>
          </div>
        </div>

        {/* 2. Enhanced Video Section with Floating UI Cards */}
        <div className="relative mt-12 lg:mt-24 max-w-5xl mx-auto px-4 group">
          
          {/* Floating UI Card 1: BINA Inteligente */}
          <div className="floating-card absolute top-[-40px] left-[-20px] lg:left-[-60px] bg-white/90 backdrop-blur-xl p-4 rounded-2xl border border-white/40 shadow-2xl z-20 max-w-[200px] hidden md:block">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                <Zap className="w-4 h-4 fill-current" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">BINA Inteligente</span>
            </div>
            <div className="flex items-end gap-1.5">
              <span className="text-xl font-bold text-gray-900">+42%</span>
              <span className="text-[10px] text-blue-600 font-bold mb-1">Efetividade</span>
            </div>
            <div className="mt-3 flex gap-1">
              <div className="h-1.5 w-full bg-blue-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 w-[85%]" />
              </div>
            </div>
          </div>

          {/* Floating UI Card 2: Monitoria */}
          <div className="floating-card absolute bottom-[-30px] right-[-20px] lg:right-[-60px] bg-white/90 backdrop-blur-xl p-4 rounded-2xl border border-white/40 shadow-2xl z-20 max-w-[180px] hidden md:block">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-emerald-100 rounded-lg text-emerald-600">
                <Activity className="w-4 h-4" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Tempo Real</span>
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-xs font-bold text-gray-900">8 operadores</span>
              </div>
              <span className="text-[10px] text-gray-500">85% em chamada</span>
            </div>
          </div>

          {/* Main Video Window with Glow */}
          <div className="relative z-10 bg-white rounded-[32px] p-2 md:p-4 border border-gray-200 shadow-2xl shadow-blue-500/5 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/5 to-transparent pointer-events-none" />
            
            {/* Window Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-400/20 rounded-full border border-red-400/40" />
                <div className="w-3 h-3 bg-yellow-400/20 rounded-full border border-yellow-400/40" />
                <div className="w-3 h-3 bg-green-400/20 rounded-full border border-green-400/40" />
              </div>
            </div>

            {/* Video Window - overlay no primeiro clique dispara VideoView e libera o player */}
            <div className="bg-gray-900 rounded-2xl aspect-video relative overflow-hidden shadow-inner border border-gray-200">
              <iframe
                src="https://player.vimeo.com/video/1157314761?badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                title="Como Usar o Onflow"
                allowFullScreen
              />
              <VideoViewOverlay contentName="Hero - Como Usar o Onflow" />
            </div>
          </div>

          {/* New Floating Element: Seta + Texto */}
          <div className="hero-helper-float absolute top-[-60px] right-[20px] lg:right-[-20px] z-20 hidden lg:block pointer-events-none">
            <div className="flex flex-col items-center">
              <span className="bg-blue-600 text-white text-[11px] font-bold px-4 py-2 rounded-full shadow-lg mb-2 whitespace-nowrap">
                Veja como é fácil e rápido usar
              </span>
              <svg width="60" height="40" viewBox="0 0 60 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-blue-600 -rotate-[15deg] scale-x-[-1]">
                <path d="M10 5C10 5 15 25 50 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 4" />
                <path d="M45 22L52 30L42 35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Waveform Refinado - Mais Sutil */}
      <div 
        ref={waveformRef}
        className="absolute bottom-0 left-0 right-0 h-[200px] flex items-end justify-center pointer-events-none opacity-20"
        style={{ zIndex: 0 }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent z-10" />
        <div className="flex items-end justify-center gap-1 w-full max-w-screen-xl mx-auto px-10">
          {waveBars.map((bar, i) => (
            <div
              key={i}
              className="w-full bg-blue-500 rounded-t-full"
              style={{
                height: bar.height,
                opacity: bar.opacity
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero; 
